import { FC, useState } from 'react';
import { Button, Form, FormInstance, Input, message, Modal, Popover, Select, Space, Switch, Typography } from 'antd';
import { TbDeviceDesktop, TbDeviceMobile } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import StatusTag from 'components/StatusTag';
import TextCopyable from 'components/TextCopyable';
import { useLocation } from 'react-router-dom';
import { checkIsSuperUser } from 'utils/auth';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import copyToClipboard from 'utils/clipboard';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import { UserResponse } from 'types';

interface ClientFormProps {
  form: FormInstance;
  onFormUpdate: (changedValues: Record<string, any>, allValues: Record<string, any>) => Promise<unknown>;
  client?: Record<string, any>;
  editing?: boolean;
}

const ClientForm: FC<ClientFormProps> = ({ form, onFormUpdate, client, editing: parentEditing }) => {
  const editing = parentEditing || !client;
  const location = useLocation();
  const isClientsPage = location.pathname === '/clients';

  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const isSuperUser = checkIsSuperUser(user);

  const [fetchingPortalLink, setFetchingPortalLink] = useState(false);

  const onGetPortalLinkHandler = async () => {
    if (!client?.uuid || fetchingPortalLink) return;

    setFetchingPortalLink(true);

    try {
      const response = await apiRequests.get(`${apiRoutes.STRIPE_CUSTOMERS}/${client.uuid}/stripe-client-portal`);
      const portalUrl = response.data?.data?.url ?? response.data?.url;

      if (!portalUrl) {
        message.error('No portal link available for this client');
        return;
      }

      const copied = await copyToClipboard(portalUrl);

      if (copied) {
        message.success('Portal link copied');
      } else {
        // The browser can refuse the copy (lost focus, expired gesture, insecure
        // context), so show the link to be copied by hand instead of losing it.
        Modal.info({
          title: 'Portal link',
          okText: 'Close',
          content: (
            <Typography.Paragraph copyable={{ text: portalUrl }} style={{ wordBreak: 'break-all', marginBottom: 0 }}>
              {portalUrl}
            </Typography.Paragraph>
          ),
        });
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setFetchingPortalLink(false);
    }
  };

  return (
    <Form
      form={form}
      onValuesChange={(changedValues, allValues) => {
        onFormUpdate(changedValues, allValues);
      }}
      autoComplete="off"
      initialValues={client}
      className={`form-label-compact${editing ? '' : ' form-label-no-margin'}`}
      size="small"
      labelAlign="left"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 15, className: 'secondary ant-form-item-control-ellipsis' }}
    >
      <Form.Item
        label={
          <>
            Company Name
            {editing && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
          </>
        }
        name="business_name"
        required={false}
        rules={
          editing
            ? [
                {
                  required: true,
                  message: 'This field is required',
                },
              ]
            : undefined
        }
      >
        {!editing && client ? <TextCopyable>{client.business_name}</TextCopyable> : <Input />}
      </Form.Item>

      {isClientsPage && (
        <Form.Item
          label={
            <>
              Status
              {editing && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
            </>
          }
          name="status"
          initialValue={!client ? 'active' : undefined}
          required={false}
          rules={
            editing
              ? [
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]
              : undefined
          }
        >
          {!editing && client ? (
            <TextCopyable text={client.status}>
              <StatusTag value={client.status} />
            </TextCopyable>
          ) : (
            <Select
              options={[
                {
                  label: 'Active',
                  value: 'active',
                },
                {
                  label: 'Inactive',
                  value: 'inactive',
                },
              ]}
            />
          )}
        </Form.Item>
      )}

      {editing || !client ? (
        <Form.Item label="Dashboard Desktop" name="dashboard_link_1">
          <Input />
        </Form.Item>
      ) : undefined}

      {editing || !client ? (
        <Form.Item label="Dashboard Mobile" name="dashboard_link_2">
          <Input />
        </Form.Item>
      ) : undefined}

      {editing || !client ? (
        <Form.Item label="Billing email" name="email">
          <Input />
        </Form.Item>
      ) : undefined}

      <Form.Item label="Allow billing portal">
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Form.Item name="allow_client_portal_access" valuePropName="checked" noStyle>
            <Switch size="small" disabled={!isSuperUser || !editing} />
          </Form.Item>

          {client && (
            <Button
              type="link"
              size="small"
              className="primary"
              style={{
                padding: 0,
                opacity: fetchingPortalLink ? 0.5 : 1,
                pointerEvents: fetchingPortalLink ? 'none' : undefined,
              }}
              onClick={onGetPortalLinkHandler}
            >
              Get portal link
            </Button>
          )}
        </div>
      </Form.Item>

      {!editing && client ? (
        <Form.Item label="Dashboards">
          <Space style={{ fontSize: '16px' }}>
            {client.dashboard_link_1 && (
              <Popover
                content={
                  <Space>
                    <Typography.Link
                      className="ant-btn ant-btn-default"
                      href={client.dashboard_link_1}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Go to link
                    </Typography.Link>

                    <Button
                      type="default"
                      size="middle"
                      onClick={() => {
                        navigator.clipboard.writeText(client.dashboard_link_1);
                        message.info('Link copied');
                      }}
                    >
                      Copy link
                    </Button>
                  </Space>
                }
              >
                <Typography.Link>
                  <TbDeviceDesktop />
                </Typography.Link>
              </Popover>
            )}

            {client.dashboard_link_2 && (
              <Popover
                content={
                  <Space>
                    <Typography.Link
                      className="ant-btn ant-btn-default"
                      href={client.dashboard_link_2}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Go to link
                    </Typography.Link>

                    <Button
                      type="default"
                      size="middle"
                      onClick={() => {
                        navigator.clipboard.writeText(client.dashboard_link_2);
                        message.info('Link copied');
                      }}
                    >
                      Copy link
                    </Button>
                  </Space>
                }
              >
                <Typography.Link>
                  <TbDeviceMobile />
                </Typography.Link>
              </Popover>
            )}
          </Space>
        </Form.Item>
      ) : undefined}
    </Form>
  );
};

export default ClientForm;
